import React, { useState, forwardRef, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  Slide,
  DialogContent,
  SlideProps,
  DialogActions,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import TuneRounded from "@mui/icons-material/TuneRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";
import WebhookOutlined from "@mui/icons-material/WebhookOutlined";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import { SvgIconComponent } from "@mui/icons-material";
import { useMainStore } from "../store/store";
import { WarningAmber } from "@mui/icons-material";
import { CustomSnackbar, Loading, Error, PageHeader } from "../components";
import TwoFactorSection from "../components/TwoFactorSection";
import { CustomSnackbarRef } from "../components/CustomSnackbar";
import { changeWebhookURL, getWebhookURL, changePassword } from "../utils/api";

const Transition = forwardRef<HTMLDivElement, SlideProps>((props, ref) => (
  <Slide direction="up" timeout={500} ref={ref} {...props} />
));

/** A titled block of settings, so the page reads as sections rather than a list. */
const SettingsSection: React.FC<{
  title: string;
  description: string;
  icon: SvgIconComponent;
  children: React.ReactNode;
  tone?: "default" | "danger";
}> = ({ title, description, icon: Icon, children, tone = "default" }) => {
  const theme = useTheme();
  const color =
    tone === "danger" ? theme.palette.error.main : theme.palette.primary.main;

  return (
    <Paper sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: "20px", mb: 2.5 }}>
      <Stack direction="row" spacing={1.75} alignItems="center" mb={2.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            borderRadius: "13px",
            color,
            bgcolor: alpha(color, 0.11),
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontSize: 17 }}>
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            {description}
          </Typography>
        </Box>
      </Stack>
      {children}
    </Paper>
  );
};

/** Webhook endpoint with its own edit lock, so a URL is never changed by accident. */
const WebhookField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
}> = ({ label, value, onChange, onSave }) => {
  const [editing, setEditing] = useState(false);

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: "text.secondary",
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://example.com/webhooks/finzora"
          disabled={!editing}
          slotProps={{
            input: {
              sx: { fontFamily: "monospace", fontSize: 13 },
            },
          }}
        />
        {editing ? (
          <>
            <Button
              variant="contained"
              onClick={() => {
                onSave();
                setEditing(false);
              }}
            >
              Save
            </Button>
            <Button variant="outlined" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="outlined" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const SettingsPage = () => {
  const snackbarRef = useRef<CustomSnackbarRef>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [oldPassword, setOldPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPasswords, setShowPasswords] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorW, setErrorW] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const { logout } = useMainStore();
  const [form, setForm] = useState<{ deposit: string; payout: string }>({
    deposit: "",
    payout: "",
  });

  const handleChangePassword = async () => {
    setError(null);
    setSuccess(false);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("Please fill in every field.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("The new password and its confirmation do not match.");
      return;
    }

    try {
      await changePassword({
        old_password: oldPassword,
        new_password: newPassword,
      });

      setSuccess(true);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      snackbarRef?.current?.showSnackbar(
        "The password has been changed",
        "success",
      );
    } catch (err) {
      setError("Could not change the password.");
      console.error("Error:", err);
      snackbarRef?.current?.showSnackbar("Changing password failed", "error");
    }
  };

  useEffect(() => {
    setLoading(true);
    getWebhookURL()
      .then((res) => setForm(res.data))
      .catch(() => setErrorW("Webhook URLs has not been loaded"))
      .finally(() => setLoading(false));
  }, []);

  /** Sends the URL that belongs to the given type — not always the deposit one. */
  const changeURL = (type: "deposit" | "payout") => {
    changeWebhookURL({ type, url: form[type] })
      .then(() =>
        snackbarRef?.current?.showSnackbar(
          `The ${type} webhook has been updated`,
          "success",
        ),
      )
      .catch(() =>
        snackbarRef?.current?.showSnackbar(
          `Updating the ${type} webhook failed`,
          "error",
        ),
      );
  };

  if (errorW) {
    return <Error error={errorW} />;
  }
  if (loading) {
    return <Loading variant="cards" />;
  }

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label="Show or hide passwords"
        size="small"
        onClick={() => setShowPasswords((value) => !value)}
        edge="end"
      >
        {showPasswords ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box
      sx={{
        width: "100%",
        paddingTop: "25px",
        maxHeight: {
          xs: "calc(var(--content-max-height) + 32.5dvh)",
          md: "82vh",
        },
        overflow: "auto",
      }}
    >
      <Box sx={{ width: "96%", margin: "0 auto", pb: 3, maxWidth: 900 }}>
        <CustomSnackbar duration={2500} ref={snackbarRef} />

        <PageHeader
          title="Settings"
          description="Account security and integration endpoints"
          icon={TuneRounded}
        />

        <TwoFactorSection
          onNotify={(message, severity) =>
            snackbarRef.current?.showSnackbar(message, severity)
          }
        />

        <SettingsSection
          title="Password"
          description="Change the password used to sign in to this cabinet"
          icon={LockOutlined}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password has been changed.
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              label="Current password"
              type={showPasswords ? "text" : "password"}
              fullWidth
              autoComplete="current-password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              slotProps={{ input: { endAdornment: passwordAdornment } }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="New password"
                type={showPasswords ? "text" : "password"}
                fullWidth
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <TextField
                label="Confirm new password"
                type={showPasswords ? "text" : "password"}
                fullWidth
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={Boolean(confirmPassword) && confirmPassword !== newPassword}
                helperText={
                  confirmPassword && confirmPassword !== newPassword
                    ? "Passwords do not match"
                    : " "
                }
              />
            </Stack>
            <Box>
              <Button onClick={handleChangePassword} variant="contained">
                Change password
              </Button>
            </Box>
          </Stack>
        </SettingsSection>

        <SettingsSection
          title="Webhooks"
          description="Where Finzora sends payment notifications"
          icon={WebhookOutlined}
        >
          <Stack spacing={2.5}>
            <WebhookField
              label="Deposit URL"
              value={form.deposit}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, deposit: value }))
              }
              onSave={() => changeURL("deposit")}
            />
            <WebhookField
              label="Payout URL"
              value={form.payout}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, payout: value }))
              }
              onSave={() => changeURL("payout")}
            />
          </Stack>
        </SettingsSection>

        <SettingsSection
          title="Session"
          description="Sign out of this device"
          icon={LogoutRounded}
          tone="danger"
        >
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutRounded />}
            onClick={() => setOpen(true)}
          >
            Log out
          </Button>
        </SettingsSection>

        <Dialog
          open={open}
          disableScrollLock
          onClose={() => setOpen(false)}
          slots={{ transition: Transition }}
          disableEnforceFocus
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Log out of the account</DialogTitle>
          <DialogContent sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmber color="error" />
            <Typography>
              You will need to sign in again to access the cabinet.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button variant="outlined" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={logout}>
              Log out
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default SettingsPage;
